import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background, Controls, MiniMap, Handle, Position,
  useNodesState, MarkerType, ConnectionMode,
} from 'reactflow';
import 'reactflow/dist/style.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../contexts/AuthContext';
import { confirmDialog, errorAlert } from '../helpers/alerts';

// Context de cores customizadas por tipo. Carregado do backend em getMapa
// e alimentado quando o operador troca uma cor no formulario. Fica em
// context para o card do no (NoTopologia) ler sem drilling de props.
const TiposCoresContext = createContext({});

// Icones bootstrap sugeridos por tipo — o usuario pode trocar por qualquer
// classe `bi-*` no formulario, mas isto cobre os casos comuns.
const ICONE_POR_TIPO = {
  servidor: 'bi-hdd-rack',
  switch: 'bi-diagram-3',
  roteador: 'bi-router',
  firewall: 'bi-shield-lock',
  banco: 'bi-database',
  vm: 'bi-pc-display',
  servico: 'bi-gear-wide-connected',
  aplicacao: 'bi-window-stack',
  nuvem: 'bi-cloud',
  rede: 'bi-broadcast-pin',
  storage: 'bi-hdd',
  outro: 'bi-box',
};

// Catalogo curado de icones bootstrap uteis para topologia de TI, agrupados
// por familia. O usuario escolhe visualmente no popover; o valor gravado
// continua sendo a classe (`bi-*`), entao qualquer icone bootstrap segue
// funcionando mesmo que nao esteja aqui.
const CATALOGO_ICONES = [
  { grupo: 'Servidores e computacao', icones: [
    'bi-hdd-rack', 'bi-hdd-rack-fill', 'bi-hdd', 'bi-hdd-fill',
    'bi-server', 'bi-pc-display', 'bi-pc-display-horizontal',
    'bi-pc', 'bi-laptop', 'bi-cpu', 'bi-cpu-fill', 'bi-motherboard',
    'bi-gpu-card', 'bi-memory',
  ] },
  { grupo: 'Rede', icones: [
    'bi-router', 'bi-router-fill', 'bi-diagram-2', 'bi-diagram-3',
    'bi-diagram-3-fill', 'bi-ethernet', 'bi-broadcast', 'bi-broadcast-pin',
    'bi-hdd-network', 'bi-hdd-network-fill', 'bi-wifi', 'bi-reception-4',
    'bi-usb-symbol',
  ] },
  { grupo: 'Seguranca', icones: [
    'bi-shield', 'bi-shield-fill', 'bi-shield-lock', 'bi-shield-lock-fill',
    'bi-shield-check', 'bi-shield-shaded', 'bi-lock', 'bi-lock-fill',
    'bi-unlock', 'bi-key', 'bi-fingerprint',
  ] },
  { grupo: 'Dados e storage', icones: [
    'bi-database', 'bi-database-fill', 'bi-database-add', 'bi-database-check',
    'bi-file-earmark-zip', 'bi-archive', 'bi-archive-fill', 'bi-folder',
    'bi-folder-fill', 'bi-collection',
  ] },
  { grupo: 'Nuvem e servicos', icones: [
    'bi-cloud', 'bi-cloud-fill', 'bi-cloud-upload', 'bi-cloud-download',
    'bi-cloud-arrow-up', 'bi-cloud-arrow-down', 'bi-cloud-check',
    'bi-globe', 'bi-globe2', 'bi-app', 'bi-app-indicator',
    'bi-window', 'bi-window-stack', 'bi-window-desktop',
    'bi-gear', 'bi-gear-fill', 'bi-gear-wide', 'bi-gear-wide-connected',
    'bi-boxes', 'bi-box', 'bi-box-seam',
  ] },
  { grupo: 'Comunicacao e monitoramento', icones: [
    'bi-telephone', 'bi-telephone-fill', 'bi-phone', 'bi-whatsapp',
    'bi-envelope', 'bi-envelope-fill', 'bi-chat-dots', 'bi-headset',
    'bi-camera-video', 'bi-camera-reels', 'bi-camera',
    'bi-printer', 'bi-printer-fill',
    'bi-display', 'bi-tv', 'bi-speedometer2', 'bi-activity',
    'bi-graph-up', 'bi-bell', 'bi-eye',
  ] },
  { grupo: 'Fluxo e outros', icones: [
    'bi-plug', 'bi-plug-fill', 'bi-lightning', 'bi-lightning-fill',
    'bi-power', 'bi-arrow-repeat', 'bi-arrow-left-right', 'bi-arrow-down-up',
    'bi-tag', 'bi-tags', 'bi-geo-alt', 'bi-people', 'bi-person',
    'bi-question-circle', 'bi-info-circle', 'bi-star',
  ] },
];

// Cor de contorno por tipo. Usadas no card do no e na miniatura.
const COR_POR_TIPO = {
  servidor: '#3ca8bc',
  switch: '#8e44ad',
  roteador: '#e67e22',
  firewall: '#e74c3c',
  banco: '#2ecc71',
  vm: '#3498db',
  servico: '#16a085',
  aplicacao: '#f39c12',
  nuvem: '#5dade2',
  rede: '#95a5a6',
  storage: '#7f8c8d',
  outro: '#bdc3c7',
};

function corDoTipo(tipo, mapaCustom) {
  const t = String(tipo || '').toLowerCase();
  return (mapaCustom && mapaCustom[t]) || COR_POR_TIPO[t] || COR_POR_TIPO.outro;
}

function iconeDoNo(no) {
  if (no?.icone && String(no.icone).trim().length) return `bi ${no.icone}`;
  return `bi ${ICONE_POR_TIPO[String(no?.tipo || '').toLowerCase()] || ICONE_POR_TIPO.outro}`;
}

// Debounce por chave: cada chave (ex.: id do no) mantem seu proprio timer,
// entao arrastar varios nos em sequencia nao faz um cancelar o save do outro.
// Chame como `fn(key, ...args)`; o `key` e usado apenas para separar os timers.
function useDebouncedByKey(fn, delay) {
  const timers = useRef(new Map());
  useEffect(() => () => {
    timers.current.forEach((id) => clearTimeout(id));
    timers.current.clear();
  }, []);
  return useCallback((key, ...args) => {
    const anterior = timers.current.get(key);
    if (anterior) clearTimeout(anterior);
    const novo = setTimeout(() => {
      timers.current.delete(key);
      fn(key, ...args);
    }, delay);
    timers.current.set(key, novo);
  }, [fn, delay]);
}

// --- Conversao dominio <-> React Flow ---------------------------------------
function nosParaFlow(nos) {
  return (nos || []).map((n) => ({
    id: String(n.id),
    type: 'topologia',
    position: { x: n.posX || 0, y: n.posY || 0 },
    data: n,
  }));
}

function conexoesParaFlow(conexoes) {
  return (conexoes || []).map((c) => ({
    id: `c-${c.id}`,
    source: String(c.origemId),
    target: String(c.destinoId),
    // Handles opcionais — nulos = default legado (origem=base, destino=topo).
    sourceHandle: c.origemHandle || 'b',
    targetHandle: c.destinoHandle || 't',
    label: c.rotulo || undefined,
    data: { conexaoId: c.id, rotulo: c.rotulo },
    markerEnd: { type: MarkerType.ArrowClosed },
  }));
}

// --- No customizado ---------------------------------------------------------
function NoTopologia({ data, selected }) {
  const tiposCores = useContext(TiposCoresContext);
  const cor = corDoTipo(data.tipo, tiposCores);
  return (
    <div
      title={`${data.nome}${data.ip ? ` — ${data.ip}` : ''}${data.observacoes ? `\n\n${data.observacoes}` : ''}`}
      style={{
        borderRadius: 10,
        border: `2px solid ${selected ? '#3ca8bc' : cor}`,
        background: 'var(--portal-surface)',
        color: 'var(--portal-text)',
        minWidth: 150,
        maxWidth: 220,
        boxShadow: selected
          ? '0 0 0 3px rgba(60,168,188,0.30)'
          : '0 4px 12px rgba(0,0,0,0.35)',
      }}
    >
      {/* 4 alcas por no: topo/esquerda como alvo e base/direita como origem.
          Com `connectionMode="loose"` (no ReactFlow), o usuario pode arrastar
          de qualquer alca para qualquer outra — a "direcao" que sai eh usada
          para o marker de seta. */}
      <Handle id="t" type="target" position={Position.Top} />
      <Handle id="l" type="target" position={Position.Left} />
      <div style={{
        background: cor, color: '#fff', padding: '5px 10px',
        borderRadius: '8px 8px 0 0', fontWeight: 700, fontSize: 11,
        letterSpacing: '.03em', textTransform: 'uppercase',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}
      >
        <span>
          <i className={iconeDoNo(data)} style={{ marginRight: 6 }} />
          {data.tipo || 'outro'}
        </span>
        {Number(data.chamadosCount) > 0 && (
          <span
            title={`${data.chamadosCount} chamado(s) vinculado(s)`}
            style={{
              background: 'rgba(0,0,0,0.35)', color: '#fff', borderRadius: 999,
              padding: '1px 7px', fontSize: 10,
            }}
          >
            <i className="bi bi-headset" style={{ marginRight: 3 }} />
            {data.chamadosCount}
          </span>
        )}
      </div>
      <div style={{ padding: '7px 10px', lineHeight: 1.35 }}>
        <div style={{ fontWeight: 700, fontSize: 13, wordBreak: 'break-word' }}>{data.nome}</div>
        {data.ip && (
          <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--portal-muted)' }}>
            {data.ip}
          </div>
        )}
        {data.ambiente && (
          <div style={{ fontSize: 10, color: 'var(--portal-muted)', marginTop: 2 }}>
            <i className="bi bi-tag" style={{ marginRight: 3 }} />{data.ambiente}
          </div>
        )}
        {data.observacoes && (
          <div
            style={{
              fontSize: 10, color: 'var(--portal-muted)', marginTop: 4,
              lineHeight: 1.35, display: '-webkit-box',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            <i className="bi bi-card-text" style={{ marginRight: 3 }} />
            {data.observacoes}
          </div>
        )}
      </div>
      <Handle id="b" type="source" position={Position.Bottom} />
      <Handle id="r" type="source" position={Position.Right} />
    </div>
  );
}

const nodeTypes = { topologia: NoTopologia };

const EDITOR_CSS = `
.topologia-page { background: var(--portal-bg); color: var(--portal-text); }
.topologia-page .tp-bar { background: var(--portal-sidebar); border-bottom: 1px solid var(--portal-border); }
.topologia-page .tp-side { background: var(--portal-surface); overflow-y: auto; }
.topologia-page .tp-side .form-label { color: var(--portal-muted); font-weight:600; font-size:.76rem; margin-bottom:2px; }
.topologia-page input, .topologia-page select, .topologia-page textarea {
  background: var(--portal-surface-soft); color: var(--portal-text);
  border:1px solid var(--portal-border); border-radius:8px;
}
.topologia-page input::placeholder, .topologia-page textarea::placeholder { color: var(--portal-muted); }
.topologia-page input:focus, .topologia-page select:focus, .topologia-page textarea:focus {
  border-color:#3ca8bc; box-shadow:0 0 0 3px rgba(60,168,188,.2); outline:none;
}
.topologia-page .react-flow__handle { background:#3ca8bc; border:2px solid var(--portal-surface); width:10px; height:10px; }
.topologia-page .react-flow__edge-path { stroke: var(--portal-muted); stroke-width:1.8; }
.topologia-page .react-flow__edge.selected .react-flow__edge-path { stroke:#3ca8bc; stroke-width:2.4; }
.topologia-page .react-flow__edge-text { fill: var(--portal-text); font-size:11px; font-weight:600; }
.topologia-page .react-flow__edge-textbg { fill: var(--portal-surface); }
.topologia-page .react-flow__controls button { background: var(--portal-surface); border-bottom:1px solid var(--portal-border); color: var(--portal-text); }
.topologia-page .react-flow__controls button:hover { background: var(--portal-surface-soft); }
.topologia-page .react-flow__controls button svg { fill: var(--portal-text); }
.topologia-page .react-flow__minimap { background: var(--portal-surface) !important; border:1px solid var(--portal-border); border-radius:8px; }
.topologia-page .react-flow__attribution { display:none; }
.topologia-page .tp-hint {
  color: var(--portal-muted); font-size:.82rem; line-height:1.5;
  background: var(--portal-surface-soft); border:1px dashed var(--portal-border);
  border-radius:10px; padding:10px;
}
.topologia-page .tp-chip {
  display:inline-flex; align-items:center; gap:.35rem; padding:.15rem .55rem;
  border-radius:999px; font-size:.72rem; background:var(--portal-surface-soft);
  border:1px solid var(--portal-border); color:var(--portal-text);
}
.topologia-page .tp-chip button {
  background:none; border:none; color:var(--portal-muted); cursor:pointer;
  padding:0; line-height:1;
}
.topologia-page .tp-btn {
  padding: .38rem .8rem; border-radius:8px; border:1px solid var(--portal-border);
  background:var(--portal-surface-soft); color:var(--portal-text); font-size:.85rem;
  cursor:pointer; display:inline-flex; align-items:center; gap:.4rem;
}
.topologia-page .tp-btn:hover { filter:brightness(1.1); }
.topologia-page .tp-btn.tp-btn-primary { background:#1E6370; color:#fff; border-color:#1E6370; }
.topologia-page .tp-btn.tp-btn-danger { background:#b43737; color:#fff; border-color:#b43737; }

.topologia-page .tp-icon-trigger {
  display:flex; align-items:center; gap:.5rem; width:100%;
  padding:.35rem .6rem; border-radius:8px; cursor:pointer;
  background: var(--portal-surface-soft); color: var(--portal-text);
  border:1px solid var(--portal-border); font-size:.85rem; text-align:left;
}
.topologia-page .tp-icon-trigger:hover { border-color:#3ca8bc; }
.topologia-page .tp-icon-trigger i.bi { font-size:1.15rem; color:#3ca8bc; }
.topologia-page .tp-icon-trigger .tp-icon-code {
  font-family: monospace; font-size:.72rem; color: var(--portal-muted);
  flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}
.topologia-page .tp-icon-picker {
  position:absolute; top:100%; left:0; right:0; margin-top:4px; z-index:20;
  background: var(--portal-surface); border:1px solid var(--portal-border);
  border-radius:10px; box-shadow: 0 10px 30px rgba(0,0,0,.45);
  max-height: 340px; overflow-y:auto; padding:.5rem;
}
.topologia-page .tp-icon-picker__group { font-size:.7rem; text-transform:uppercase;
  letter-spacing:.05em; color: var(--portal-muted); margin: .5rem .25rem .3rem; font-weight:700; }
.topologia-page .tp-icon-picker__group:first-child { margin-top: .1rem; }
.topologia-page .tp-icon-picker__grid {
  display:grid; grid-template-columns: repeat(6, 1fr); gap:.25rem;
}
.topologia-page .tp-icon-picker__btn {
  background: var(--portal-surface-soft); border:1px solid transparent;
  border-radius:6px; padding:.4rem; cursor:pointer; color: var(--portal-text);
  display:flex; align-items:center; justify-content:center; font-size:1.1rem;
  transition: background .1s, border-color .1s;
}
.topologia-page .tp-icon-picker__btn:hover { background: var(--portal-surface); border-color:#3ca8bc; }
.topologia-page .tp-icon-picker__btn.is-selected { background:#3ca8bc; color:#fff; border-color:#3ca8bc; }
.topologia-page .tp-icon-picker__actions {
  display:flex; gap:.3rem; padding-top:.4rem; border-top:1px solid var(--portal-border);
  margin-top:.4rem;
}
.topologia-page .tp-icon-picker__search {
  padding:.3rem .5rem; font-size:.8rem; margin-bottom:.35rem;
}

/* Legenda flutuante — resumo dos tipos em uso e suas cores. */
.topologia-page .tp-legend {
  position: absolute; top: 10px; left: 10px; z-index: 10;
  background: var(--portal-surface); border: 1px solid var(--portal-border);
  border-radius: 10px; box-shadow: 0 6px 20px rgba(0,0,0,.35);
  color: var(--portal-text); min-width: 180px; max-width: 240px;
  overflow: hidden;
}
.topologia-page .tp-legend__header {
  display: flex; align-items: center; justify-content: space-between; gap: .5rem;
  padding: 6px 10px; background: var(--portal-surface-soft);
  border-bottom: 1px solid var(--portal-border); font-size: .75rem;
  font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  cursor: pointer; user-select: none;
}
.topologia-page .tp-legend__header i { font-size: .85rem; opacity: .7; }
.topologia-page .tp-legend__body { padding: 6px 8px; max-height: 320px; overflow-y: auto; }
.topologia-page .tp-legend__empty {
  padding: 10px; text-align: center; font-size: .78rem; color: var(--portal-muted);
}
.topologia-page .tp-legend__item {
  display: flex; align-items: center; gap: .5rem;
  padding: 4px 6px; border-radius: 6px; font-size: .8rem;
}
.topologia-page .tp-legend__item:hover { background: var(--portal-surface-soft); }
.topologia-page .tp-legend__swatch {
  width: 14px; height: 14px; border-radius: 4px; flex-shrink: 0;
  border: 1px solid rgba(0,0,0,.25);
}
.topologia-page .tp-legend__label { flex: 1; text-transform: capitalize; }
.topologia-page .tp-legend__count {
  font-size: .7rem; color: var(--portal-muted); font-variant-numeric: tabular-nums;
}

/* Toggle canvas/lista no topo. */
.topologia-page .tp-view-toggle {
  display: inline-flex; align-items: center; padding: 2px; gap: 2px;
  background: var(--portal-surface-soft); border: 1px solid var(--portal-border);
  border-radius: 8px;
}
.topologia-page .tp-view-toggle button {
  border: none; background: transparent; color: var(--portal-muted);
  border-radius: 6px; padding: .28rem .55rem; font-size: .8rem; cursor: pointer;
  display: inline-flex; align-items: center; gap: .3rem;
}
.topologia-page .tp-view-toggle button:hover { color: var(--portal-text); }
.topologia-page .tp-view-toggle button.is-active {
  background: var(--portal-surface); color: var(--portal-text);
  box-shadow: 0 1px 3px rgba(0,0,0,.25);
}

/* Modo lista — reusa o painel lateral existente para editar/excluir/duplicar. */
.topologia-page .tp-list { padding: 12px 16px; overflow-y: auto; height: 100%; }
.topologia-page .tp-list__toolbar {
  display: flex; align-items: center; gap: .5rem; margin-bottom: 10px; flex-wrap: wrap;
}
.topologia-page .tp-list__search {
  flex: 1; min-width: 200px; padding: .35rem .6rem;
  border-radius: 8px; border: 1px solid var(--portal-border);
  background: var(--portal-surface-soft); color: var(--portal-text); font-size: .85rem;
}
.topologia-page .tp-list__table {
  width: 100%; border-collapse: separate; border-spacing: 0;
  background: var(--portal-surface); border: 1px solid var(--portal-border);
  border-radius: 10px; overflow: hidden;
}
.topologia-page .tp-list__table th, .topologia-page .tp-list__table td {
  padding: 8px 10px; font-size: .82rem; text-align: left; vertical-align: middle;
  border-bottom: 1px solid var(--portal-border);
}
.topologia-page .tp-list__table thead th {
  background: var(--portal-surface-soft); color: var(--portal-muted);
  font-size: .7rem; text-transform: uppercase; letter-spacing: .04em; font-weight: 700;
}
.topologia-page .tp-list__table tbody tr { cursor: pointer; }
.topologia-page .tp-list__table tbody tr:hover { background: var(--portal-surface-soft); }
.topologia-page .tp-list__table tbody tr.is-selected {
  background: rgba(60,168,188,.12); box-shadow: inset 3px 0 0 #3ca8bc;
}
.topologia-page .tp-list__table tbody tr:last-child td { border-bottom: none; }
.topologia-page .tp-list__chip {
  display: inline-flex; align-items: center; gap: .35rem;
  padding: 2px 8px; border-radius: 999px; font-size: .72rem; font-weight: 600;
  color: #fff; text-transform: uppercase; letter-spacing: .03em;
}
.topologia-page .tp-list__ellipsis {
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden; text-overflow: ellipsis; max-width: 380px;
  color: var(--portal-muted); font-size: .78rem;
}
.topologia-page .tp-list__actions {
  display: inline-flex; gap: 4px;
}
.topologia-page .tp-list__actions button {
  background: transparent; border: 1px solid var(--portal-border); color: var(--portal-text);
  border-radius: 6px; padding: 3px 7px; cursor: pointer; font-size: .78rem;
}
.topologia-page .tp-list__actions button:hover { border-color: #3ca8bc; }
.topologia-page .tp-list__actions button.is-danger { color: #ff8787; border-color: rgba(255,135,135,.4); }
.topologia-page .tp-list__actions button.is-danger:hover { border-color: #b43737; }
.topologia-page .tp-list__empty {
  padding: 30px 10px; text-align: center; color: var(--portal-muted); font-size: .85rem;
}

/* Cabecalho de grupo na lista aninhada por tipo. */
.topologia-page .tp-list__group-header td {
  background: var(--portal-surface-soft); font-weight: 700; font-size: .75rem;
  letter-spacing: .04em; text-transform: uppercase; color: var(--portal-text);
  cursor: pointer; user-select: none; padding: 8px 10px;
  border-bottom: 1px solid var(--portal-border);
}
.topologia-page .tp-list__group-header td:hover { background: var(--portal-surface); }
.topologia-page .tp-list__group-cell {
  display: flex; align-items: center; gap: .5rem;
}
.topologia-page .tp-list__group-cell i.bi-chevron-right,
.topologia-page .tp-list__group-cell i.bi-chevron-down {
  font-size: .75rem; opacity: .6;
}
.topologia-page .tp-list__group-swatch {
  width: 12px; height: 12px; border-radius: 3px;
  border: 1px solid rgba(0,0,0,.25); flex-shrink: 0;
}
.topologia-page .tp-list__group-count {
  margin-left: auto; font-size: .7rem; color: var(--portal-muted);
  font-variant-numeric: tabular-nums; letter-spacing: 0; text-transform: none; font-weight: 500;
}
/* Recuo das linhas de detalhe dentro de um grupo. */
.topologia-page .tp-list__table tbody tr.tp-list__nested td:first-child { padding-left: 26px; }
`;

// Legenda flutuante: agrupa os nos por tipo, ordena pela contagem e mostra
// a cor efetiva (custom -> fallback hardcoded). Colapsavel para nao atrapalhar
// no mapa cheio.
function Legenda({ nodes, tiposCores }) {
  const [colapsada, setColapsada] = useState(false);

  const itens = useMemo(() => {
    const bucket = new Map();
    (nodes || []).forEach((n) => {
      const t = String(n.data?.tipo || 'outro').trim().toLowerCase() || 'outro';
      bucket.set(t, (bucket.get(t) || 0) + 1);
    });
    return Array.from(bucket.entries())
      .map(([tipo, total]) => ({ tipo, total, cor: corDoTipo(tipo, tiposCores) }))
      .sort((a, b) => b.total - a.total || a.tipo.localeCompare(b.tipo));
  }, [nodes, tiposCores]);

  return (
    <div className="tp-legend">
      <div
        className="tp-legend__header"
        onClick={() => setColapsada((v) => !v)}
        title={colapsada ? 'Mostrar legenda' : 'Recolher legenda'}
      >
        <span><i className="bi bi-palette me-1" /> Legenda</span>
        <i className={`bi ${colapsada ? 'bi-chevron-down' : 'bi-chevron-up'}`} />
      </div>
      {!colapsada && (
        <div className="tp-legend__body">
          {itens.length === 0 ? (
            <div className="tp-legend__empty">Sem nos no mapa.</div>
          ) : itens.map(({ tipo, total, cor }) => (
            <div key={tipo} className="tp-legend__item">
              <span className="tp-legend__swatch" style={{ background: cor }} />
              <span className="tp-legend__label">{tipo}</span>
              <span className="tp-legend__count">{total}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Modo lista — visao alternativa ao canvas, aninhada por categoria (tipo).
// Reusa o mesmo estado do canvas (nodes, selectedId, painel lateral). A
// acao "focar no mapa" alterna de volta para o canvas com o no selecionado.
function ListaNos({
  nodes, tiposCores, selectedId,
  onSelecionar, onDuplicar, onExcluir, onVerNoMapa,
}) {
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState('nome'); // nome | chamados (interno ao grupo)
  const [colapsados, setColapsados] = useState(() => new Set()); // tipos recolhidos

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return nodes;
    return nodes.filter((n) => {
      const d = n.data || {};
      return [d.nome, d.tipo, d.ip, d.ambiente, d.responsavel, d.observacoes]
        .filter(Boolean).join(' ').toLowerCase().includes(termo);
    });
  }, [nodes, busca]);

  // Agrupa por tipo (normalizado). Ordena grupos pelo nome do tipo; dentro
  // do grupo aplica a ordenacao escolhida (nome ou chamados).
  const grupos = useMemo(() => {
    const bucket = new Map();
    filtradas.forEach((n) => {
      const t = String(n.data?.tipo || 'outro').trim().toLowerCase() || 'outro';
      if (!bucket.has(t)) bucket.set(t, []);
      bucket.get(t).push(n);
    });
    const arr = Array.from(bucket.entries()).map(([tipo, itens]) => {
      const ordenados = itens.slice().sort((a, b) => {
        const da = a.data || {};
        const db = b.data || {};
        if (ordem === 'chamados') {
          return (Number(db.chamadosCount) || 0) - (Number(da.chamadosCount) || 0)
            || String(da.nome || '').localeCompare(String(db.nome || ''), 'pt-BR');
        }
        return String(da.nome || '').localeCompare(String(db.nome || ''), 'pt-BR');
      });
      return { tipo, itens: ordenados, cor: corDoTipo(tipo, tiposCores) };
    });
    arr.sort((a, b) => b.itens.length - a.itens.length || a.tipo.localeCompare(b.tipo, 'pt-BR'));
    return arr;
  }, [filtradas, ordem, tiposCores]);

  const alternarGrupo = (tipo) => {
    setColapsados((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(tipo)) proximo.delete(tipo); else proximo.add(tipo);
      return proximo;
    });
  };
  const recolherTodos = () => setColapsados(new Set(grupos.map((g) => g.tipo)));
  const expandirTodos = () => setColapsados(new Set());

  return (
    <div className="tp-list">
      <div className="tp-list__toolbar">
        <input
          className="tp-list__search"
          placeholder="Buscar por nome, tipo, IP, ambiente..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select
          className="tp-list__search"
          style={{ flex: 'none', width: 200 }}
          value={ordem}
          onChange={(e) => setOrdem(e.target.value)}
        >
          <option value="nome">Ordem: nome</option>
          <option value="chamados">Ordem: chamados</option>
        </select>
        <button type="button" className="tp-btn" onClick={expandirTodos} title="Expandir todos">
          <i className="bi bi-arrows-expand" />
        </button>
        <button type="button" className="tp-btn" onClick={recolherTodos} title="Recolher todos">
          <i className="bi bi-arrows-collapse" />
        </button>
        <span style={{ color: 'var(--portal-muted)', fontSize: '.78rem' }}>
          {filtradas.length} / {nodes.length} no(s) · {grupos.length} categoria(s)
        </span>
      </div>

      {filtradas.length === 0 ? (
        <div className="tp-list__empty">
          <i className="bi bi-inbox" style={{ fontSize: '1.6rem', display: 'block', marginBottom: 6 }} />
          Nenhum no encontrado.
        </div>
      ) : (
        <table className="tp-list__table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>ID</th>
              <th>Nome</th>
              <th style={{ width: 130 }}>IP</th>
              <th style={{ width: 120 }}>Ambiente</th>
              <th style={{ width: 90, textAlign: 'center' }}>Chamados</th>
              <th>Descricao</th>
              <th style={{ width: 130 }}>&nbsp;</th>
            </tr>
          </thead>
          {grupos.map((g) => {
            const aberto = !colapsados.has(g.tipo);
            return (
              <tbody key={g.tipo}>
                <tr
                  className="tp-list__group-header"
                  onClick={() => alternarGrupo(g.tipo)}
                >
                  <td colSpan={7}>
                    <div className="tp-list__group-cell">
                      <i className={`bi ${aberto ? 'bi-chevron-down' : 'bi-chevron-right'}`} />
                      <span className="tp-list__group-swatch" style={{ background: g.cor }} />
                      <span>{g.tipo}</span>
                      <span className="tp-list__group-count">
                        {g.itens.length} no(s)
                      </span>
                    </div>
                  </td>
                </tr>
                {aberto && g.itens.map((n) => {
                  const d = n.data || {};
                  const sel = selectedId === n.id;
                  return (
                    <tr
                      key={n.id}
                      className={`tp-list__nested ${sel ? 'is-selected' : ''}`}
                      onClick={() => onSelecionar(n.id)}
                    >
                      <td style={{ color: 'var(--portal-muted)', fontVariantNumeric: 'tabular-nums' }}>
                        {n.id}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          <i className={iconeDoNo(d)} style={{ marginRight: 6, color: g.cor }} />
                          {d.nome || '(sem nome)'}
                        </div>
                        {d.responsavel && (
                          <div style={{ fontSize: '.72rem', color: 'var(--portal-muted)' }}>
                            <i className="bi bi-person me-1" />{d.responsavel}
                          </div>
                        )}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '.78rem' }}>
                        {d.ip || <span style={{ color: 'var(--portal-muted)' }}>—</span>}
                      </td>
                      <td>{d.ambiente || <span style={{ color: 'var(--portal-muted)' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>
                        {Number(d.chamadosCount) > 0 ? (
                          <span className="tp-list__chip" style={{ background: '#b43737' }}>
                            <i className="bi bi-headset" /> {d.chamadosCount}
                          </span>
                        ) : <span style={{ color: 'var(--portal-muted)' }}>—</span>}
                      </td>
                      <td>
                        {d.observacoes
                          ? <div className="tp-list__ellipsis">{d.observacoes}</div>
                          : <span style={{ color: 'var(--portal-muted)' }}>—</span>}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="tp-list__actions">
                          <button type="button" title="Focar no mapa" onClick={() => onVerNoMapa(n.id)}>
                            <i className="bi bi-crosshair" />
                          </button>
                          <button type="button" title="Duplicar" onClick={() => onDuplicar(n.id)}>
                            <i className="bi bi-files" />
                          </button>
                          <button type="button" className="is-danger" title="Excluir" onClick={() => onExcluir(n.id)}>
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            );
          })}
        </table>
      )}
    </div>
  );
}

function noVazio() {
  return {
    nome: '', tipo: 'servidor', icone: '', ip: '',
    ambiente: '', so: '', responsavel: '', observacoes: '',
  };
}

// Lista fixa de SOs suportados pelo Prometheus HTTP SD (mesma que o backend
// aceita em siti_topologia_no.so). Se um dia entrar outro SO, ajustar aqui,
// no service topologiaService.js (SO_VALIDOS) e no prometheusSdService
// (PORTA_POR_SO) — os tres precisam bater.
const SOS_SUPORTADOS = [
  { chave: 'linux',   nome: 'Linux' },
  { chave: 'windows', nome: 'Windows' },
  { chave: 'mac',     nome: 'Mac' },
];

// --- Pagina principal -------------------------------------------------------
export default function Topologia() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges] = useState([]);
  const [tiposSugeridos, setTiposSugeridos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [tiposCores, setTiposCores] = useState({});
  const [modoVis, setModoVis] = useState(() => {
    try { return localStorage.getItem('topologia-view') === 'lista' ? 'lista' : 'canvas'; }
    catch { return 'canvas'; }
  });
  const alternarModo = (novo) => {
    setModoVis(novo);
    try { localStorage.setItem('topologia-view', novo); } catch { /* ignore */ }
  };
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [selectedId, setSelectedId] = useState(null); // id do no selecionado (string)
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [criando, setCriando] = useState(false);
  const [novoNo, setNovoNo] = useState(noVazio());
  const [salvando, setSalvando] = useState(false);
  const [chamados, setChamados] = useState([]);
  const [chamadoIdInput, setChamadoIdInput] = useState('');
  const [editandoRotulo, setEditandoRotulo] = useState('');

  const persistirPosicao = useDebouncedByKey(async (id, posX, posY) => {
    try {
      await axios.patch(`${API}/topologia/nos/${id}/posicao`, { posX, posY });
    } catch {
      // Silencioso — a proxima acao explicita (edicao/carregar) sincroniza.
    }
  }, 400);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/topologia/mapa`);
      const data = res.data?.data || {};
      setNodes(nosParaFlow(data.nos || []));
      setEdges(conexoesParaFlow(data.conexoes || []));
      setTiposSugeridos(data.tiposSugeridos || []);
      setCategorias(data.categorias || []);
      setTiposCores(data.tiposCores || {});
      setErro('');
    } catch (e) {
      setErro('Nao foi possivel carregar a topologia.');
    } finally {
      setLoading(false);
    }
  }, [setNodes]);

  useEffect(() => { carregar(); }, [carregar]);

  // Ao selecionar um no, carrega detalhes (inclui chamados vinculados).
  useEffect(() => {
    if (!selectedId) { setChamados([]); return; }
    let ativo = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/topologia/nos/${selectedId}`);
        if (!ativo) return;
        setChamados(res.data?.data?.chamados || []);
      } catch {
        if (ativo) setChamados([]);
      }
    })();
    return () => { ativo = false; };
  }, [selectedId]);

  // Persistencia de posicao: usamos `onNodeDragStop` em vez de escutar
  // `onNodesChange`. No fim do drag, o react-flow dispara `dragging: false`
  // SEM o campo `position` (`updateNodePositions(items, false, false)`),
  // entao um guard `ch.position` derruba o save. `onNodeDragStop` sempre
  // entrega o no com a posicao final.
  const onNodeDragStop = useCallback((_event, node) => {
    if (!node?.position) return;
    persistirPosicao(node.id, node.position.x, node.position.y);
  }, [persistirPosicao]);

  // Quando o usuario arrasta uma selecao multipla, o react-flow entrega uma
  // lista com todos os nos movidos. Persistimos cada um separadamente — o
  // debounce por chave garante que todos sobrevivem.
  const onSelectionDragStop = useCallback((_event, movidos) => {
    (movidos || []).forEach((n) => {
      if (n?.position) persistirPosicao(n.id, n.position.x, n.position.y);
    });
  }, [persistirPosicao]);

  // Nova aresta: cria conexao no backend e adiciona.
  const onConnect = useCallback(async (params) => {
    if (!params.source || !params.target || params.source === params.target) return;
    try {
      const res = await axios.post(`${API}/topologia/conexoes`, {
        origemId: Number(params.source),
        destinoId: Number(params.target),
        origemHandle: params.sourceHandle || null,
        destinoHandle: params.targetHandle || null,
      });
      const c = res.data?.data;
      if (c) {
        setEdges((es) => [
          ...es,
          {
            id: `c-${c.id}`,
            source: String(c.origemId),
            target: String(c.destinoId),
            sourceHandle: c.origemHandle || 'b',
            targetHandle: c.destinoHandle || 't',
            label: c.rotulo || undefined,
            data: { conexaoId: c.id, rotulo: c.rotulo },
            markerEnd: { type: MarkerType.ArrowClosed },
          },
        ]);
      }
    } catch (e) {
      errorAlert({
        title: 'Nao foi possivel conectar',
        text: e.response?.data?.message || 'Falha ao criar conexao.',
      });
    }
  }, []);

  const onEdgeClick = useCallback((_, edge) => {
    setSelectedEdgeId(edge.id);
    setSelectedId(null);
    setEditandoRotulo(edge.data?.rotulo || '');
  }, []);

  const onNodeClick = useCallback((_, node) => {
    setSelectedId(node.id);
    setSelectedEdgeId(null);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedId(null);
    setSelectedEdgeId(null);
  }, []);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedId) || null,
    [nodes, selectedId],
  );

  const selectedEdge = useMemo(
    () => edges.find((e) => e.id === selectedEdgeId) || null,
    [edges, selectedEdgeId],
  );

  // --- Acoes de no ----------------------------------------------------------
  const abrirCriacao = () => {
    setCriando(true);
    setSelectedId(null);
    setSelectedEdgeId(null);
    setNovoNo(noVazio());
  };

  const salvarNovoNo = async (e) => {
    e?.preventDefault?.();
    if (!novoNo.nome.trim()) {
      errorAlert({ title: 'Nome obrigatorio', text: 'Informe um nome para o no.' });
      return;
    }
    setSalvando(true);
    try {
      const res = await axios.post(`${API}/topologia/nos`, {
        ...novoNo,
        posX: 120 + Math.random() * 240,
        posY: 120 + Math.random() * 240,
      });
      const n = res.data?.data;
      if (n) {
        setNodes((ns) => [...ns, ...nosParaFlow([n])]);
        setSelectedId(String(n.id));
      }
      setCriando(false);
    } catch (err) {
      errorAlert({
        title: 'Falha ao criar',
        text: err.response?.data?.message || 'Nao foi possivel criar o no.',
      });
    } finally {
      setSalvando(false);
    }
  };

  const atualizarCampoNo = (id, patch) => {
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n)));
  };

  const salvarNoAtual = async () => {
    if (!selectedNode) return;
    const d = selectedNode.data;
    if (!d.nome?.trim()) {
      errorAlert({ title: 'Nome obrigatorio', text: 'Informe um nome para o no.' });
      return;
    }
    setSalvando(true);
    try {
      const res = await axios.put(`${API}/topologia/nos/${selectedNode.id}`, {
        nome: d.nome,
        tipo: d.tipo,
        icone: d.icone,
        ip: d.ip,
        ambiente: d.ambiente,
        so: d.so || null,
        responsavel: d.responsavel,
        observacoes: d.observacoes,
        posX: selectedNode.position.x,
        posY: selectedNode.position.y,
      });
      const atualizado = res.data?.data;
      if (atualizado) {
        setNodes((ns) => ns.map((n) => (n.id === String(atualizado.id)
          ? { ...n, data: { ...n.data, ...atualizado } }
          : n)));
      }
      Swal.fire({ icon: 'success', title: 'No salvo', timer: 1000, showConfirmButton: false });
    } catch (err) {
      errorAlert({
        title: 'Falha ao salvar',
        text: err.response?.data?.message || 'Nao foi possivel salvar.',
      });
    } finally {
      setSalvando(false);
    }
  };

  const removerNo = useCallback(async (id) => {
    const alvoId = id || selectedNode?.id;
    if (!alvoId) return;
    const alvo = nodes.find((n) => n.id === alvoId);
    const nome = alvo?.data?.nome || 'este no';
    const r = await confirmDialog({
      title: 'Remover no?',
      text: `"${nome}" sera removido junto com suas conexoes e vinculos.`,
      confirmButtonText: 'Remover',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/topologia/nos/${alvoId}`);
      setNodes((ns) => ns.filter((n) => n.id !== alvoId));
      setEdges((es) => es.filter((e) => e.source !== alvoId && e.target !== alvoId));
      if (selectedId === alvoId) setSelectedId(null);
    } catch (err) {
      errorAlert({
        title: 'Falha ao remover',
        text: err.response?.data?.message || 'Nao foi possivel remover.',
      });
    }
  }, [nodes, selectedNode, selectedId, setNodes, setEdges]);

  // Duplica um no (por id ou o selecionado). Backend copia atributos e
  // desloca a posicao — o clone aparece proximo do original e ja fica
  // selecionado para o operador ajustar.
  const duplicarNo = useCallback(async (id) => {
    const alvoId = id || selectedNode?.id;
    if (!alvoId) return;
    try {
      const res = await axios.post(`${API}/topologia/nos/${alvoId}/duplicar`);
      const clone = res.data?.data;
      if (clone) {
        setNodes((ns) => [...ns, ...nosParaFlow([clone])]);
        setSelectedId(String(clone.id));
      }
    } catch (err) {
      errorAlert({
        title: 'Falha ao duplicar',
        text: err.response?.data?.message || 'Nao foi possivel duplicar.',
      });
    }
  }, [selectedNode, setNodes]);

  // Abre modal (SweetAlert2) para o operador batizar uma categoria nova. O
  // backend slugifica o nome — nao precisamos calcular a chave aqui, so pedir
  // o rotulo humano. Depois de criar, ja seleciona a categoria nova no form
  // que estava aberto (setNovoNo/atualizarCampoNo), evitando duplo clique.
  const criarCategoria = useCallback(async ({ aplicarEm }) => {
    const { value: nome, isConfirmed } = await Swal.fire({
      title: 'Nova categoria',
      input: 'text',
      inputLabel: 'Nome da categoria',
      inputPlaceholder: 'Ex.: Balanceador, Impressora, IoT...',
      inputAttributes: { maxlength: 80 },
      showCancelButton: true,
      confirmButtonText: 'Criar',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => (!v || v.trim().length < 2 ? 'Nome muito curto.' : undefined),
    });
    if (!isConfirmed || !nome) return;
    try {
      const res = await axios.post(`${API}/topologia/categorias`, { nome: nome.trim() });
      const nova = res.data?.data;
      if (!nova) return;
      setCategorias((prev) => {
        if (prev.some((c) => c.chave === nova.chave)) return prev;
        return [...prev, nova].sort((a, b) => a.nome.localeCompare(b.nome));
      });
      // Ja aplica no formulario aberto (novo ou edicao).
      if (aplicarEm === 'novo') setNovoNo((v) => ({ ...v, tipo: nova.chave }));
      else if (aplicarEm === 'edicao' && selectedId) {
        atualizarCampoNo(String(selectedId), { tipo: nova.chave });
      }
    } catch (err) {
      errorAlert({
        title: 'Nao foi possivel criar categoria',
        text: err.response?.data?.message || 'Erro ao criar categoria.',
      });
    }
  }, [selectedId]);

  // Salva a cor personalizada da categoria (tipo). Todos os nos do mesmo
  // tipo passam a exibir a nova cor imediatamente (via TiposCoresContext).
  const salvarCorTipo = useCallback(async (tipo, cor) => {
    const t = String(tipo || '').trim().toLowerCase();
    if (!t) return;
    setTiposCores((prev) => ({ ...prev, [t]: cor })); // update otimista
    try {
      await axios.patch(`${API}/topologia/tipos/${encodeURIComponent(t)}/cor`, { cor });
    } catch (err) {
      errorAlert({
        title: 'Falha ao salvar cor',
        text: err.response?.data?.message || 'Nao foi possivel salvar a cor.',
      });
    }
  }, []);

  // --- Acoes de aresta ------------------------------------------------------
  const salvarRotulo = async () => {
    if (!selectedEdge) return;
    const conexaoId = selectedEdge.data?.conexaoId;
    try {
      await axios.patch(`${API}/topologia/conexoes/${conexaoId}`, { rotulo: editandoRotulo });
      setEdges((es) => es.map((e) => (e.id === selectedEdge.id
        ? { ...e, label: editandoRotulo || undefined, data: { ...e.data, rotulo: editandoRotulo } }
        : e)));
      Swal.fire({ icon: 'success', title: 'Rotulo salvo', timer: 900, showConfirmButton: false });
    } catch (err) {
      errorAlert({
        title: 'Falha ao salvar',
        text: err.response?.data?.message || 'Nao foi possivel salvar o rotulo.',
      });
    }
  };

  const removerConexao = async () => {
    if (!selectedEdge) return;
    const conexaoId = selectedEdge.data?.conexaoId;
    const r = await confirmDialog({
      title: 'Remover conexao?',
      text: 'A ligacao entre os dois nos sera desfeita.',
      confirmButtonText: 'Remover',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/topologia/conexoes/${conexaoId}`);
      setEdges((es) => es.filter((e) => e.id !== selectedEdge.id));
      setSelectedEdgeId(null);
    } catch (err) {
      errorAlert({
        title: 'Falha ao remover',
        text: err.response?.data?.message || 'Nao foi possivel remover a conexao.',
      });
    }
  };

  // --- Chamados vinculados --------------------------------------------------
  const vincularChamado = async () => {
    if (!selectedNode) return;
    const idNum = Number(String(chamadoIdInput).trim());
    if (!Number.isInteger(idNum) || idNum <= 0) {
      errorAlert({ title: 'ID invalido', text: 'Informe um ID numerico do chamado.' });
      return;
    }
    try {
      const res = await axios.post(`${API}/topologia/nos/${selectedNode.id}/chamados`, {
        chamadoId: idNum,
      });
      setChamados(res.data?.data || []);
      setChamadoIdInput('');
      // Atualiza contagem no card do canvas.
      atualizarCampoNo(selectedNode.id, {
        chamadosCount: (res.data?.data || []).length,
      });
    } catch (err) {
      errorAlert({
        title: 'Nao foi possivel vincular',
        text: err.response?.data?.message || 'Verifique o ID do chamado.',
      });
    }
  };

  const desvincularChamado = async (chamadoId) => {
    if (!selectedNode) return;
    try {
      const res = await axios.delete(
        `${API}/topologia/nos/${selectedNode.id}/chamados/${chamadoId}`,
      );
      setChamados(res.data?.data || []);
      atualizarCampoNo(selectedNode.id, {
        chamadosCount: (res.data?.data || []).length,
      });
    } catch (err) {
      errorAlert({
        title: 'Falha ao desvincular',
        text: err.response?.data?.message || 'Nao foi possivel desvincular.',
      });
    }
  };

  // --- Render ---------------------------------------------------------------
  return (
    <TiposCoresContext.Provider value={tiposCores}>
    <div className="topologia-page d-flex flex-column" style={{ height: 'calc(100vh - 60px)', position: 'relative' }}>
      <style>{EDITOR_CSS}</style>

      <div className="tp-bar d-flex align-items-center gap-2 px-3 py-2">
        <div style={{ flex: 1 }}>
          <span className="section-header__eyebrow">TI</span>
          <h1 style={{ margin: 0, fontSize: '1.15rem' }}>Topologia da infraestrutura</h1>
          <div style={{ fontSize: '.78rem', color: 'var(--portal-muted)' }}>
            Desenhe servidores, redes e servicos; conecte-os arrastando das alcas.
          </div>
        </div>
        <div className="tp-view-toggle" role="tablist" aria-label="Modo de visualizacao">
          <button
            type="button"
            className={modoVis === 'canvas' ? 'is-active' : ''}
            onClick={() => alternarModo('canvas')}
            title="Mapa (canvas)"
          >
            <i className="bi bi-diagram-3" /> Mapa
          </button>
          <button
            type="button"
            className={modoVis === 'lista' ? 'is-active' : ''}
            onClick={() => alternarModo('lista')}
            title="Lista"
          >
            <i className="bi bi-list-ul" /> Lista
          </button>
        </div>
        <button type="button" className="tp-btn" onClick={carregar} disabled={loading}>
          <i className="bi bi-arrow-clockwise" /> Atualizar
        </button>
        <button type="button" className="tp-btn tp-btn-primary" onClick={abrirCriacao}>
          <i className="bi bi-plus-lg" /> Novo no
        </button>
      </div>

      {erro && (
        <div className="px-3 py-1" style={{ background: 'rgba(220,53,69,.15)', color: '#ff8787', fontSize: '.85rem' }}>
          <i className="bi bi-exclamation-triangle me-1" /> {erro}
        </div>
      )}

      <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
        <div className="flex-grow-1" style={{ minWidth: 0, position: 'relative' }}>
          {!loading && modoVis === 'canvas' && <Legenda nodes={nodes} tiposCores={tiposCores} />}
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--portal-muted)' }}>
              <div className="screen-loader__spinner" />
              <p>Carregando topologia...</p>
            </div>
          ) : modoVis === 'lista' ? (
            <ListaNos
              nodes={nodes}
              tiposCores={tiposCores}
              selectedId={selectedId}
              onSelecionar={(id) => { setSelectedId(id); setSelectedEdgeId(null); setCriando(false); }}
              onDuplicar={duplicarNo}
              onExcluir={removerNo}
              onVerNoMapa={(id) => {
                setSelectedId(id);
                setSelectedEdgeId(null);
                setCriando(false);
                alternarModo('canvas');
              }}
            />
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onNodesChange={onNodesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onNodeDragStop={onNodeDragStop}
              onSelectionDragStop={onSelectionDragStop}
              onEdgeClick={onEdgeClick}
              onPaneClick={onPaneClick}
              fitView
              deleteKeyCode={null}
              connectionMode={ConnectionMode.Loose}
            >
              <Background color="#243B43" gap={18} />
              <Controls />
              <MiniMap
                pannable
                zoomable
                nodeColor={(n) => corDoTipo(n.data?.tipo, tiposCores)}
                maskColor="rgba(14,27,31,.7)"
              />
            </ReactFlow>
          )}
        </div>

        {/* Painel lateral: criar, editar no, editar aresta ou dica */}
        <div className="tp-side p-3" style={{ width: 340, borderLeft: '1px solid var(--portal-border)' }}>
          {criando ? (
            <FormNo
              titulo="Novo no"
              valor={novoNo}
              onChange={(patch) => setNovoNo((v) => ({ ...v, ...patch }))}
              onSalvar={salvarNovoNo}
              onCancelar={() => setCriando(false)}
              salvando={salvando}
              categorias={categorias}
              onCriarCategoria={() => criarCategoria({ aplicarEm: 'novo' })}
              corTipo={corDoTipo(novoNo.tipo, tiposCores)}
              onMudarCorTipo={salvarCorTipo}
            />
          ) : selectedNode ? (
            <FormNo
              titulo="Editar no"
              valor={selectedNode.data}
              onChange={(patch) => atualizarCampoNo(selectedNode.id, patch)}
              onSalvar={salvarNoAtual}
              onCancelar={() => setSelectedId(null)}
              onRemover={() => removerNo(selectedNode.id)}
              onDuplicar={() => duplicarNo(selectedNode.id)}
              salvando={salvando}
              categorias={categorias}
              onCriarCategoria={() => criarCategoria({ aplicarEm: 'edicao' })}
              corTipo={corDoTipo(selectedNode.data.tipo, tiposCores)}
              onMudarCorTipo={salvarCorTipo}
              chamados={chamados}
              chamadoIdInput={chamadoIdInput}
              setChamadoIdInput={setChamadoIdInput}
              vincularChamado={vincularChamado}
              desvincularChamado={desvincularChamado}
            />
          ) : selectedEdge ? (
            <FormAresta
              rotulo={editandoRotulo}
              onChange={setEditandoRotulo}
              onSalvar={salvarRotulo}
              onRemover={removerConexao}
              onCancelar={() => setSelectedEdgeId(null)}
            />
          ) : (
            <div className="tp-hint">
              <i className="bi bi-hand-index-thumb me-1" />
              Selecione um <strong>no</strong> para editar ou uma <strong>aresta</strong> para
              rotular. Para <strong>conectar</strong> dois nos, arraste da alca inferior
              de um ate a alca superior do outro. Para <strong>criar</strong> um no,
              use o botao no topo.
            </div>
          )}
        </div>
      </div>
    </div>
    </TiposCoresContext.Provider>
  );
}

// --- Formulario do no -------------------------------------------------------
function FormNo({
  titulo, valor, onChange, onSalvar, onCancelar, onRemover, onDuplicar,
  salvando, categorias, onCriarCategoria, corTipo, onMudarCorTipo,
  chamados, chamadoIdInput, setChamadoIdInput, vincularChamado, desvincularChamado,
}) {
  // Se o no ja tem um `tipo` que nao esta no catalogo (linha antiga criada
  // antes da migration 057 pegar, ou categoria removida), garante uma option
  // pra ele — senao o <select> pula silenciosamente pro primeiro item e
  // o operador acha que o dado sumiu.
  const base = Array.isArray(categorias) && categorias.length > 0 ? categorias : [];
  const tipoAtual = valor?.tipo || '';
  const lista = (base.some((c) => c.chave === tipoAtual) || !tipoAtual)
    ? base
    : [...base, { chave: tipoAtual, nome: `${tipoAtual} (fora do catalogo)` }];
  const ehServidor = tipoAtual === 'servidor';
  return (
    <form onSubmit={onSalvar}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>{titulo}</strong>
        <div className="d-flex gap-1">
          {onDuplicar && (
            <button
              type="button"
              className="tp-btn"
              style={{ padding: '.2rem .55rem', fontSize: '.75rem' }}
              onClick={onDuplicar}
              title="Duplicar no"
            >
              <i className="bi bi-files" />
            </button>
          )}
          {onRemover && (
            <button
              type="button"
              className="tp-btn tp-btn-danger"
              style={{ padding: '.2rem .55rem', fontSize: '.75rem' }}
              onClick={onRemover}
              title="Remover no"
            >
              <i className="bi bi-trash" />
            </button>
          )}
        </div>
      </div>

      <div className="mb-2">
        <label className="form-label">Nome</label>
        <input
          className="form-control form-control-sm"
          value={valor.nome || ''}
          maxLength={150}
          onChange={(e) => onChange({ nome: e.target.value })}
        />
      </div>

      <div className="mb-2">
        <label className="form-label">Categoria</label>
        <div className="d-flex gap-2 align-items-stretch">
          <select
            className="form-select form-select-sm"
            value={valor.tipo || ''}
            onChange={(e) => {
              const novoTipo = e.target.value;
              // Trocou a categoria pra algo que nao e servidor -> zera SO,
              // senao o backend recebe SO orfao e o guard/validator recusa
              // (ou o dado fica ambiguo no banco).
              const patch = { tipo: novoTipo };
              if (novoTipo !== 'servidor') patch.so = '';
              onChange(patch);
            }}
            style={{ flex: 1 }}
          >
            {lista.map((c) => (
              <option key={c.chave} value={c.chave}>{c.nome}</option>
            ))}
          </select>
          {onCriarCategoria && (
            <button
              type="button"
              className="tp-btn"
              title="Criar nova categoria"
              onClick={onCriarCategoria}
              style={{ padding: '.2rem .55rem', fontSize: '.85rem' }}
            >
              <i className="bi bi-plus-lg" />
            </button>
          )}
          {onMudarCorTipo && (
            <label
              title="Cor da categoria (compartilhada por todos os nos deste tipo)"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 34, borderRadius: 8, border: '1px solid var(--portal-border)',
                background: corTipo || 'transparent', cursor: 'pointer',
                position: 'relative', overflow: 'hidden',
              }}
            >
              <i className="bi bi-palette-fill" style={{ color: '#fff', fontSize: '.9rem', textShadow: '0 0 3px rgba(0,0,0,.5)' }} />
              <input
                type="color"
                value={corTipo || '#3ca8bc'}
                onChange={(e) => onMudarCorTipo(valor.tipo, e.target.value)}
                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
              />
            </label>
          )}
        </div>
        <small style={{ color: 'var(--portal-muted)' }}>Categoria fixa; use o botao <i className="bi bi-plus-lg" /> para criar uma nova.</small>
      </div>

      {ehServidor && (
        <div className="mb-2">
          <label className="form-label">Sistema operacional</label>
          <select
            className="form-select form-select-sm"
            value={valor.so || ''}
            onChange={(e) => onChange({ so: e.target.value })}
          >
            <option value="">— nao informado —</option>
            {SOS_SUPORTADOS.map((s) => (
              <option key={s.chave} value={s.chave}>{s.nome}</option>
            ))}
          </select>
          <small style={{ color: 'var(--portal-muted)' }}>
            Preenchido = entra automaticamente no scrape do Prometheus (com IP definido).
          </small>
        </div>
      )}

      <div className="mb-2">
        <label className="form-label">Icone</label>
        <IconPicker
          valor={valor.icone}
          tipoFallback={valor.tipo}
          onChange={(icone) => onChange({ icone })}
        />
      </div>

      <div className="mb-2">
        <label className="form-label">Endereco IP</label>
        <input
          className="form-control form-control-sm"
          value={valor.ip || ''}
          maxLength={64}
          placeholder="192.168.0.10"
          onChange={(e) => onChange({ ip: e.target.value })}
        />
      </div>

      <div className="mb-2">
        <label className="form-label">Ambiente</label>
        <input
          className="form-control form-control-sm"
          value={valor.ambiente || ''}
          maxLength={50}
          placeholder="producao, homologacao..."
          onChange={(e) => onChange({ ambiente: e.target.value })}
        />
      </div>

      <div className="mb-2">
        <label className="form-label">Responsavel</label>
        <input
          className="form-control form-control-sm"
          value={valor.responsavel || ''}
          maxLength={150}
          onChange={(e) => onChange({ responsavel: e.target.value })}
        />
      </div>

      <div className="mb-2">
        <label className="form-label">Observacoes</label>
        <textarea
          className="form-control form-control-sm"
          rows={3}
          value={valor.observacoes || ''}
          maxLength={4000}
          onChange={(e) => onChange({ observacoes: e.target.value })}
        />
      </div>

      <div className="d-flex gap-2 mt-3">
        <button type="submit" className="tp-btn tp-btn-primary" disabled={salvando} style={{ flex: 1 }}>
          <i className="bi bi-save" /> {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        <button type="button" className="tp-btn" onClick={onCancelar}>
          Cancelar
        </button>
      </div>

      {/* Chamados vinculados aparecem so no modo edicao */}
      {onRemover && (
        <>
          <hr />
          <div className="mb-2">
            <strong style={{ fontSize: '.85rem' }}>
              <i className="bi bi-headset me-1" />Chamados vinculados
            </strong>
            <div style={{ fontSize: '.75rem', color: 'var(--portal-muted)' }}>
              {chamados.length === 0
                ? 'Nenhum chamado vinculado.'
                : `${chamados.length} chamado(s) associado(s) a este no.`}
            </div>
          </div>

          {chamados.map((c) => (
            <div
              key={c.id}
              style={{
                border: '1px solid var(--portal-border)', borderRadius: 8,
                padding: '.4rem .6rem', marginBottom: 6, fontSize: '.8rem',
                display: 'flex', gap: '.5rem', alignItems: 'center',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>#{c.id} — {c.titulo}</div>
                <div style={{ fontSize: '.7rem', color: 'var(--portal-muted)' }}>
                  {c.status}{c.prioridade ? ` · ${c.prioridade}` : ''}
                </div>
              </div>
              <button
                type="button"
                className="tp-chip"
                title="Desvincular"
                onClick={() => desvincularChamado(c.id)}
                style={{ cursor: 'pointer' }}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
          ))}

          <div className="d-flex gap-2 mt-2">
            <input
              type="number"
              className="form-control form-control-sm"
              placeholder="ID do chamado"
              value={chamadoIdInput}
              onChange={(e) => setChamadoIdInput(e.target.value)}
            />
            <button type="button" className="tp-btn" onClick={vincularChamado}>
              <i className="bi bi-plus-lg" /> Vincular
            </button>
          </div>
        </>
      )}
    </form>
  );
}

// --- Formulario da aresta ---------------------------------------------------
function FormAresta({ rotulo, onChange, onSalvar, onRemover, onCancelar }) {
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>Conexao</strong>
        <button
          type="button"
          className="tp-btn tp-btn-danger"
          style={{ padding: '.2rem .55rem', fontSize: '.75rem' }}
          onClick={onRemover}
          title="Remover conexao"
        >
          <i className="bi bi-trash" />
        </button>
      </div>
      <div className="mb-2">
        <label className="form-label">Rotulo (opcional)</label>
        <input
          className="form-control form-control-sm"
          value={rotulo}
          maxLength={100}
          placeholder="ex: HTTPS 443, VPN, depende de..."
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      <div className="d-flex gap-2 mt-3">
        <button type="button" className="tp-btn tp-btn-primary" onClick={onSalvar} style={{ flex: 1 }}>
          <i className="bi bi-save" /> Salvar
        </button>
        <button type="button" className="tp-btn" onClick={onCancelar}>
          Fechar
        </button>
      </div>
    </div>
  );
}

// --- Seletor visual de icones -----------------------------------------------
// Mostra o icone atual como botao; ao clicar abre um popover com o catalogo
// agrupado. Se o usuario nao tiver escolhido nada, mostra o icone padrao do
// tipo do no (o mesmo que o canvas usa). Busca livre filtra por nome da classe.
function IconPicker({ valor, tipoFallback, onChange }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const wrapRef = useRef(null);

  const iconeAtual = valor && valor.trim().length
    ? valor.trim()
    : (ICONE_POR_TIPO[String(tipoFallback || '').toLowerCase()] || ICONE_POR_TIPO.outro);
  const iconePersonalizado = !!(valor && valor.trim().length);

  // Fecha ao clicar fora.
  useEffect(() => {
    if (!aberto) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [aberto]);

  const termo = busca.trim().toLowerCase();
  const gruposFiltrados = termo
    ? CATALOGO_ICONES
        .map((g) => ({ ...g, icones: g.icones.filter((i) => i.toLowerCase().includes(termo)) }))
        .filter((g) => g.icones.length > 0)
    : CATALOGO_ICONES;

  const selecionar = (classe) => {
    onChange(classe);
    setAberto(false);
    setBusca('');
  };

  return (
    <div style={{ position: 'relative' }} ref={wrapRef}>
      <button
        type="button"
        className="tp-icon-trigger"
        onClick={() => setAberto((v) => !v)}
        title="Escolher icone"
      >
        <i className={`bi ${iconeAtual}`} />
        <span className="tp-icon-code">
          {iconePersonalizado ? iconeAtual : `${iconeAtual} (padrao do tipo)`}
        </span>
        <i className="bi bi-chevron-down" style={{ fontSize: '.75rem', opacity: .6 }} />
      </button>

      {aberto && (
        <div className="tp-icon-picker">
          <input
            className="form-control form-control-sm tp-icon-picker__search"
            placeholder="Buscar (ex: cloud, hdd, shield...)"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            autoFocus
          />

          {gruposFiltrados.length === 0 && (
            <div style={{ padding: '.5rem', fontSize: '.8rem', color: 'var(--portal-muted)', textAlign: 'center' }}>
              Nenhum icone encontrado.
            </div>
          )}

          {gruposFiltrados.map((g) => (
            <div key={g.grupo}>
              <div className="tp-icon-picker__group">{g.grupo}</div>
              <div className="tp-icon-picker__grid">
                {g.icones.map((classe) => (
                  <button
                    key={classe}
                    type="button"
                    className={`tp-icon-picker__btn ${iconeAtual === classe ? 'is-selected' : ''}`}
                    title={classe}
                    onClick={() => selecionar(classe)}
                  >
                    <i className={`bi ${classe}`} />
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="tp-icon-picker__actions">
            <button
              type="button"
              className="tp-btn"
              style={{ flex: 1, fontSize: '.75rem' }}
              onClick={() => selecionar('')}
              title="Voltar a usar o icone padrao do tipo do no"
            >
              <i className="bi bi-arrow-counterclockwise" /> Usar padrao do tipo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
